import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
    Box,
    Heading,
    Input,
    VStack,
    Select,
    Flex,
    useBreakpointValue,
    Button
} from '@chakra-ui/react';
import { useEvents } from '../hooks/useEvents';
import { groupEventsByDate } from '../utils/eventUtils';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';
import { TradingEventsTable } from '../components/events/TradingEventsTable';
import { TradingPasswordProtection } from '../components/TradingPasswordProtection';
import { ruleSetLabel } from '../utils/ruleSet';

const TradingDashboardContent: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
    const [selectedSport, setSelectedSport] = useState<string>('');
    const [selectedRuleSet, setSelectedRuleSet] = useState<string>('');

    // Use our custom hook to manage events data (without cancel functionality)
    const { events, isLoading } = useEvents(selectedDate);

    // Distinct sport values from the loaded events, and rule sets for the selected sport
    const sportOptions = useMemo(
        () => Array.from(new Set(events.map(e => e.sport?.name).filter(Boolean) as string[])).sort(),
        [events]
    );
    const ruleSetOptions = useMemo(
        () => Array.from(new Set(
            events
                .filter(e => !selectedSport || e.sport?.name === selectedSport)
                .map(e => e.competition?.ruleSet)
                .filter(Boolean) as string[]
        )).sort(),
        [events, selectedSport]
    );

    // Client-side filtering by sport, then group by date and filter by rule set
    const filteredEvents = useMemo(
        () => events.filter(e => !selectedSport || e.sport?.name === selectedSport),
        [events, selectedSport]
    );
    const groupedEvents = groupEventsByDate(filteredEvents, selectedRuleSet);
    
    // Use responsive values for UI elements
    const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const headingSize = useBreakpointValue({ base: 'sm', md: 'md' });
    
    return (
        <Box p={{ base: 2, md: 4 }}>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                <Flex 
                    direction={{ base: 'column', sm: 'row' }} 
                    justify="space-between"
                    align={{ base: 'flex-start', sm: 'center' }}
                    gap={{ base: 2, sm: 0 }}
                >
                    <Heading size={headingSize}>Trading Dashboard</Heading>
                </Flex>

                <Box mb={{ base: 3, md: 4 }}>
                    <Flex 
                        direction={{ base: 'column', md: 'row' }} 
                        gap={{ base: 2, md: 4 }}
                        align={{ base: 'stretch', md: 'center' }}
                    >
                        <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '200px' }}
                            width={{ base: '100%', md: 'auto' }}
                        />
                        <Select
                            value={selectedSport}
                            onChange={(e) => { setSelectedSport(e.target.value); setSelectedRuleSet(''); }}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '150px' }}
                            width={{ base: '100%', md: 'auto' }}
                            placeholder="All sports"
                        >
                            {sportOptions.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </Select>
                        <Select
                            value={selectedRuleSet}
                            onChange={(e) => setSelectedRuleSet(e.target.value)}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '150px' }}
                            width={{ base: '100%', md: 'auto' }}
                            placeholder="All RuleSets"
                        >
                            {ruleSetOptions.map((rs) => (
                                <option key={rs} value={rs}>{ruleSetLabel(rs)}</option>
                            ))}
                        </Select>
                        <Flex 
                            direction={{ base: 'row', md: 'row' }} 
                            gap={2}
                            justify={{ base: 'flex-start', md: 'flex-start' }}
                            width={{ base: '100%', md: 'auto' }}
                        >
                            {selectedDate && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedDate('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear Date
                                </Button>
                            )}
                            {selectedSport && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedSport('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear Sport
                                </Button>
                            )}
                            {selectedRuleSet && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedRuleSet('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear RuleSet
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                </Box>

                {isLoading ? (
                    <LoadingSpinner message="Loading events..." />
                ) : Object.keys(groupedEvents).length === 0 ? (
                    <EmptyState message="No events found for the selected date and filters." />
                ) : (
                    Object.entries(groupedEvents)
                        .sort(([dateA], [dateB]) => dateB.localeCompare(dateA)) // Sort in descending order
                        .map(([date, dateEvents]) => (
                            <TradingEventsTable 
                                key={date} 
                                date={date} 
                                events={dateEvents} 
                            />
                        ))
                )}
            </VStack>
        </Box>
    );
};

export const TradingDashboardPage: React.FC = () => {
    return (
        <TradingPasswordProtection>
            <TradingDashboardContent />
        </TradingPasswordProtection>
    );
};
